import { Vector2 } from "../../common/vector2.js";
import { ImgID } from "../../image_loader/img_id.js";
import { Easing } from "../../transforms/transform_base.js";
import { TransformScale } from "../../transforms/transform_scale.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIAdaptiveElementsByTextHorisontal } from "../common_ui_elements/ui_adaptive_elements_by_text_horisontal.js";
import { UIElement } from "../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogManager } from "./dialog_manager.js";
import { InviteFriendDialog } from "./invite_friend_dialog.js";
import { MultiballBooster } from "../../cumulative_boosters/boosters/multiball_booster.js";
import { InfiniteBallsBooster } from "../../cumulative_boosters/boosters/infinite_balls_booster.js";
import { showRewarded } from "../../ads/rewarded.js";
import { areAdsBlockedOffline } from "../../ads/offline_ads.js";
import { hideOfflineAdTooltip, showAdUnavailableTooltip, showOfflineAdTooltip } from "../tooltip/offline_ad_tooltip.js";
import {
  isRewardedCooldownActive,
  rewardedCooldownRemainingMs,
  rewardedCooldownTimerText,
  startRewardedCooldown,
  type RewardedCooldownId,
} from "../../ads/rewarded_booster_cooldown.js";
import {
  logBoosterRewardedFunnelEvent,
  logRewardedCompleted,
} from "../../analytics/appsflyer_events.js";
import { AnalyticsEventName } from "../../analytics/index.js";
import { rewardBooster } from "../../offline/economy_gateway.js";
import { adoptEconomyFromPlayer } from "../../api/player_sync.js";
import type { RewardBoosterResponse, RewardBoosterType } from "../../api/dto/player.dto.js";
import type { BoosterPriceOfferDto, PurchaseBoosterResponse } from "../../api/dto/cumulative_boosters.dto.js";
import { CumulativeBoosterID } from "../../cumulative_boosters/cumulative_booster_id.js";
import { CumulativeBoosterService } from "../../cumulative_boosters/cumulative_booster_service.js";
import { ConveyorConfigService } from "../../conveyor_config/conveyor_config_service.js";
import { StatisticsStorage } from "../../storage/statistics_storage.js";

export interface BuyBoosterDialogOptions
{
  booster_type: BuyBoosterType;
  booster_id: CumulativeBoosterID;
  offer: BoosterPriceOfferDto;
  source: BuyBoosterRewardSource;
  on_buy?: ( response: PurchaseBoosterResponse ) => void;
  on_reward?: ( quantity: number ) => void;
}

export type BuyBoosterRewardSource = "game_start" | "in_game" | "debug" | "unknown";

export enum BuyBoosterType
{
  Firework,
  FiveBalls,
  BigBall,
  InfiniteShots,
}

function getRewardedCooldownId( booster_type: BuyBoosterType ): RewardedCooldownId
{
  switch ( booster_type )
  {
    case BuyBoosterType.Firework: return 'firework';
    case BuyBoosterType.FiveBalls: return 'tenballs';
    case BuyBoosterType.BigBall: return 'bigball';
    case BuyBoosterType.InfiniteShots: return 'infiniteballs';
  }
}

export function getDefaultBoosterEffectValue( booster_type: BuyBoosterType ): number | null
{
  switch ( booster_type )
  {
    case BuyBoosterType.FiveBalls:
      return MultiballBooster.DEFAULT_BALL_COUNT;
    case BuyBoosterType.InfiniteShots:
      return InfiniteBallsBooster.DEFAULT_DURATION_SECONDS;
    default:
      return null;
  }
}

export function getBoosterTextVariableParams(
  booster_type: BuyBoosterType,
  effect_value?: number | null
): string[]
{
  const value = effect_value ?? getDefaultBoosterEffectValue( booster_type );
  return value === null ? [] : [ String( value ) ];
}

interface BuyBoosterVisualConfig
{
  title_key: string;
  description_key: string;
  icon_id: ImgID;
  analytics_id: RewardBoosterType;
}

const BOOSTER_VISUAL_CONFIGS: Record<BuyBoosterType, BuyBoosterVisualConfig> = {
  [BuyBoosterType.Firework]: {
    title_key: "BOOSTER.FIREWORK",
    description_key: "DESCRIPTION.FIREWORK",
    icon_id: ImgID.UI_GameStartFireworkBooster,
    analytics_id: "firework",
  },
  [BuyBoosterType.FiveBalls]: {
    title_key: "BOOSTER.FIVE_BALLS",
    description_key: "DESCRIPTION.FIVE_BALLS",
    icon_id: ImgID.UI_GameStartMultiballsBooster,
    analytics_id: "five_balls",
  },
  [BuyBoosterType.BigBall]: {
    title_key: "BOOSTER.BIG_BALL",
    description_key: "DESCRIPTION.BIG_BALL",
    icon_id: ImgID.UI_GameStartBigBallBooster,
    analytics_id: "big_ball",
  },
  [BuyBoosterType.InfiniteShots]: {
    title_key: "BOOSTER.INFINITE_SHOTS",
    description_key: "DESCRIPTION.INFINITE_SHOTS",
    icon_id: ImgID.UI_GameStartInfiniteBallsBooster,
    analytics_id: "infinite_shots",
  },
};

const MINE_VISUAL_CONFIG: BuyBoosterVisualConfig = {
  title_key: "BOOSTER.MINE",
  description_key: "DESCRIPTION.MINE",
  icon_id: ImgID.UI_PassiveBoosterMine,
  analytics_id: "mine",
};

const FREEZE_VISUAL_CONFIG: BuyBoosterVisualConfig = {
  title_key: "BOOSTER.FREEZE",
  description_key: "DESCRIPTION.FREEZE",
  icon_id: ImgID.UI_GameFreezeBooster,
  analytics_id: "freeze",
};

export class BuyBoosterDialog extends AbstractDialog
{
  private static readonly DESIGN_W = 1080;
  private static readonly DESIGN_H = 1944;

  // Exact transform from the 1080 x 1944 design.
  private static readonly BG_X = 86;
  private static readonly BG_Y = 419;
  private static readonly BG_W = 909;
  private static readonly BG_H = 1286;
  private static readonly HIT_X = 86;
  private static readonly HIT_Y = 375;
  private static readonly HIT_W = 915;
  private static readonly HIT_H = 1330;

  private readonly price: number;
  private readonly quantity: number;
  private readonly booster_id: CumulativeBoosterID;
  private readonly offer_id: number;
  private readonly on_buy?: ( response: PurchaseBoosterResponse ) => void;
  private readonly on_reward?: ( quantity: number ) => void;
  private readonly booster_type: BuyBoosterType;
  private readonly effect_value: number | null;
  private readonly reward_quantity = 1;
  private readonly reward_source: BuyBoosterRewardSource;
  private readonly rewarded_placement: string;
  private readonly rewarded_cooldown_id: RewardedCooldownId;
  private readonly reward_available: boolean;
  private readonly visual_config: BuyBoosterVisualConfig;
  private purchase_inflight = false;
  private close_btn!: UIImage;
  private reward_inflight = false;
  private reward_offer_shown_tracked = false;
  private reward_offer_clicked = false;
  private reward_granted = false;
  private reward_offer_closed_tracked = false;
  private reward_btn: UINineSliceElement | null = null;
  private reward_active_content: UIElement | null = null;
  private reward_cooldown_content: UIElement | null = null;
  private reward_cooldown_text: UITextElement | null = null;
  private reward_cooldown_visible = false;
  private shown_reward_cooldown_timer = "";
  private reward_debug_render_reason: string | null = null;
  private next_reward_sync_at = 0;
  private shown_ads_offline = false;

  constructor( options: BuyBoosterDialogOptions )
  {
    const body_w = LayoutScaler.widthFromDesign( BuyBoosterDialog.DESIGN_W );
    const body_h = LayoutScaler.heightFromDesign( BuyBoosterDialog.DESIGN_H );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h );

    this.price = options.offer.price;
    this.quantity = options.offer.amount;
    this.booster_id = options.booster_id;
    this.offer_id = options.offer.id;
    this.on_buy = options.on_buy;
    this.on_reward = options.on_reward;
    this.booster_type = options.booster_type;
    this.visual_config = options.booster_id === CumulativeBoosterID.Mine
      ? MINE_VISUAL_CONFIG
      : options.booster_id === CumulativeBoosterID.Freeze
        ? FREEZE_VISUAL_CONFIG
        : BOOSTER_VISUAL_CONFIGS[options.booster_type];
    this.reward_source = options.source;
    this.rewarded_placement = `booster_${this.reward_source}_${this.visual_config.analytics_id}`;
    this.rewarded_cooldown_id = getRewardedCooldownId( this.booster_type );
    this.reward_available = this.reward_source === "game_start" || this.reward_source === "in_game";
    this.effect_value = this.getConfiguredEffectValue();

    this.createBackground();
    this.createTopBanner();
    this.createInfoFrame();
    this.createDescription();
    this.createBuyButton();
    this.createCloseButton();
  }

  private getConfiguredEffectValue(): number | null
  {
    switch ( this.booster_id )
    {
      case CumulativeBoosterID.Multiball:
        return CumulativeBoosterService.getBoosterProperties(
          CumulativeBoosterID.Multiball
        )?.ball_count ?? getDefaultBoosterEffectValue( this.booster_type );
      case CumulativeBoosterID.InfiniteBalls:
        return CumulativeBoosterService.getBoosterProperties(
          CumulativeBoosterID.InfiniteBalls
        )?.duration_seconds ?? getDefaultBoosterEffectValue( this.booster_type );
      case CumulativeBoosterID.Freeze:
        return ConveyorConfigService.get().freeze_sec;
      default:
        return getDefaultBoosterEffectValue( this.booster_type );
    }
  }

  private createBackground(): void
  {
    const bg = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( BuyBoosterDialog.BG_X ),
      LayoutScaler.yFromDesign( BuyBoosterDialog.BG_Y ),
      LayoutScaler.widthFromDesign( BuyBoosterDialog.BG_W ),
      LayoutScaler.heightFromDesign( BuyBoosterDialog.BG_H ),
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( bg );
  }

  private createTopBanner(): void
  {
    const design_w = 717.08;
    const design_h = 254.52;
    const design_x = ( BuyBoosterDialog.DESIGN_W - design_w ) / 2;
    const design_y = 400.48;

    const banner_w = LayoutScaler.widthFromDesign( design_w );
    const banner_h = LayoutScaler.heightFromDesign( design_h );
    const banner = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      banner_w,
      banner_h,
      ImgID.UI_BoosterNameBg
    );

    const title = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 45 ),
      LayoutScaler.yFromDesign( 12 ),
      banner_w - LayoutScaler.widthFromDesign( 90 ),
      banner_h - LayoutScaler.heightFromDesign( 24 ),
      {
        localization_key: this.visual_config.title_key,
        text_variable_params: getBoosterTextVariableParams( this.booster_type, this.effect_value ),
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.booster_dialog_quantity_outline,
        auto_wrap: true,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 48 ),
        line_height: 0.95,
      }
    );
    banner.addChild( title );
    this.addChild( banner );
  }

  private createInfoFrame(): void
  {
    const design_w = 780;
    const design_h = 354;
    const design_x = ( BuyBoosterDialog.DESIGN_W - design_w ) / 2;
    const design_y = 724;

    const frame_w = LayoutScaler.widthFromDesign( design_w );
    const frame_h = LayoutScaler.heightFromDesign( design_h );
    const frame = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      frame_w,
      frame_h,
      ImgID.UI_BoosterPaternedBg
    );

    const design_icon_size = 256;
    const icon_size = LayoutScaler.widthFromDesign( design_icon_size );
    const icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( ( design_w - design_icon_size ) / 2 ),
      LayoutScaler.yFromDesign( ( design_h - design_icon_size ) / 2 ),
      icon_size,
      icon_size,
      this.visual_config.icon_id
    );

    const quantity_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 159 ),
      LayoutScaler.yFromDesign( 153.95 ),
      LayoutScaler.widthFromDesign( 84 ),
      LayoutScaler.heightFromDesign( 98 ),
      {
        text: `x${this.quantity}`,
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.booster_dialog_quantity_outline,
        auto_wrap: false,
      }
    );

    icon.addChild( quantity_text );
    frame.addChild( icon );
    this.addChild( frame );
  }

  private createDescription(): void
  {
    const description = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 177 ),
      LayoutScaler.yFromDesign( 1100 ),
      LayoutScaler.widthFromDesign( 727 ),
      LayoutScaler.heightFromDesign( 183 ),
      {
        localization_key: this.visual_config.description_key,
        text_variable_params: getBoosterTextVariableParams( this.booster_type, this.effect_value ),
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.booster_dialog_quantity_outline,
        auto_wrap: true,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 38 ),
        line_height: 1.05,
      }
    );
    this.addChild( description );
  }

  private createBuyButton(): void
  {
    const design_y = 1355;
    const design_w = 700;
    const design_h = 280;

    const btn_w = LayoutScaler.widthFromDesign( design_w );
    const btn_h = LayoutScaler.heightFromDesign( design_h );
    const btn_x = ( this.rect.getW() - btn_w ) / 2;
    const btn = new UIImage(
      this.ctx,
      btn_x,
      LayoutScaler.yFromDesign( design_y ),
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
    );

    const text_options = {
      font_size: LayoutScaler.textSizeFromDesign( 60 ),
      font_weight: 800,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.menu_button_outline,
      ...TextStyleScheme.buttonShadow(),
    };

    const buy_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      { localization_key: "BUY", ...text_options }
    );
    const coin_size = LayoutScaler.widthFromDesign( 104 );
    const coin = new UIImage( this.ctx, 0, 0, coin_size, coin_size, ImgID.IconCoin );
    const price_text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      { text: this.price.toString(), ...text_options }
    );
    const content = new UIAdaptiveElementsByTextHorisontal(
      this.ctx,
      LayoutScaler.xFromDesign( 9 ),
      LayoutScaler.yFromDesign( 8 ),
      btn_w,
      btn_h,
      [ buy_text, coin, price_text ],
      0
    );

    btn.addChild( content, true );
    btn.setOnClick( async () =>
    {
      if ( StatisticsStorage.load().coins < this.price )
      {
        DialogManager.openDialog( new InviteFriendDialog() );
        return;
      }

      if ( this.purchase_inflight )
        return;

      this.purchase_inflight = true;
      const response = await CumulativeBoosterService.purchase(
        this.booster_id,
        this.offer_id
      );

      if ( response === null )
      {
        // The outcome of a network timeout can be ambiguous: the server may
        // already have committed the purchase. Close the locked dialog instead
        // of allowing an unsafe immediate retry.
        DialogManager.closeDialog( "buy_booster_failed" );
        return;
      }

      // Keep the button locked for the close animation. Resetting the flag
      // here allowed a second tap to buy the same pack again (3 -> 6).
      DialogManager.closeDialog( "buy_booster" );
      this.on_buy?.( response );
    } );
    this.addChild( btn );
  }

  private createRewardButton(): void
  {
    const design_x = 203;
    const design_y = 1159;
    const design_w = 670;
    const design_h = 250;

    const btn_w = LayoutScaler.widthFromDesign( design_w );
    const btn_h = LayoutScaler.heightFromDesign( design_h );
    const btn = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( design_x ),
      LayoutScaler.yFromDesign( design_y ),
      btn_w,
      btn_h,
      ImgID.UI_PurpleFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const video_icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 84 ),
      LayoutScaler.yFromDesign( 85 ),
      LayoutScaler.widthFromDesign( 104 ),
      LayoutScaler.heightFromDesign( 93 ),
      ImgID.UI_VideoIcon
    );
    const reward_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 190 ),
      LayoutScaler.yFromDesign( 28 ),
      LayoutScaler.widthFromDesign( 420 ),
      LayoutScaler.heightFromDesign( 210 ),
      {
        localization_key: "BUY_ONE_ADS",
        text_variable_params: [ String( this.reward_quantity ) ],
        font_size: LayoutScaler.textSizeFromDesign( 53 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 0.95,
        outline_color: ColorScheme.purple_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );

    const active_content = new UIElementHolder( this.ctx, 0, 0, btn_w, btn_h );
    active_content.addChild( video_icon, true );
    active_content.addChild( reward_text, true );
    const clock_size = LayoutScaler.widthFromDesign( 104 );
    const clock = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 84 ),
      ( btn_h - clock_size ) / 2,
      clock_size,
      clock_size,
      ImgID.UI_ClockFrame
    );
    const hand_h = LayoutScaler.heightFromDesign( 80 );
    const hand_w = hand_h * ( 24 / 73 );
    const hand = new UIImage(
      this.ctx,
      ( clock_size - hand_w ) / 2,
      ( clock_size - hand_h ) / 2,
      hand_w,
      hand_h,
      ImgID.UI_ClockHand
    );
    hand.enableRotation();
    hand.setRotationImmediate( Math.PI / 6 );
    clock.addChild( hand );

    const cooldown_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 225 ),
      LayoutScaler.yFromDesign( 75 ),
      LayoutScaler.widthFromDesign( 350 ),
      LayoutScaler.heightFromDesign( 100 ),
      {
        text: "00:00:00",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    const cooldown_content = new UIElementHolder( this.ctx, 0, 0, btn_w, btn_h );
    cooldown_content.addChild( clock, true );
    cooldown_content.addChild( cooldown_text, true );
    active_content.setClickable( false );
    cooldown_content.setClickable( false );
    btn.addChild( active_content, true );

    btn.setOnClick( () => { void this.watchRewardedAd( btn ); } );
    this.addChild( btn );
    this.reward_btn = btn;
    this.reward_active_content = active_content;
    this.reward_cooldown_content = cooldown_content;
    this.reward_cooldown_text = cooldown_text;
    this.syncRewardButtonState();
  }

  public override update( now: number ): void
  {
    super.update( now );
    if ( now < this.next_reward_sync_at ) return;
    this.next_reward_sync_at = now + 250;
    this.syncRewardButtonState();
  }

  private syncRewardButtonState(): void
  {
    if ( !this.reward_btn || !this.reward_active_content || !this.reward_cooldown_content )
      return;

    const remaining = rewardedCooldownRemainingMs( this.rewarded_cooldown_id );
    const cooldown_active = remaining > 0;
    const timer_text = rewardedCooldownTimerText( remaining );

    if ( timer_text !== this.shown_reward_cooldown_timer )
    {
      this.shown_reward_cooldown_timer = timer_text;
      this.reward_cooldown_text?.setText( timer_text );
      this.reward_debug_render_reason = `timer:${timer_text}`;
      this.invalidateSubtree();
      console.log( '[RewardedCooldownUI][BuyBoosterDialog] timer', {
        cooldown_id: this.rewarded_cooldown_id,
        timer_text,
        remaining_ms: remaining,
        cooldown_active,
        dialog_should_render: this.shouldBeRendered(),
      } );
    }

    if ( cooldown_active !== this.reward_cooldown_visible )
    {
      this.reward_cooldown_visible = cooldown_active;
      if ( cooldown_active )
      {
        this.reward_btn.removeChild( this.reward_active_content );
        this.reward_btn.addChild( this.reward_cooldown_content, true );
      }
      else
      {
        this.reward_btn.removeChild( this.reward_cooldown_content );
        this.reward_btn.addChild( this.reward_active_content, true );
      }
      this.reward_debug_render_reason = `state:${cooldown_active ? 'cooldown' : 'available'}`;
      this.invalidateSubtree();
      console.log( '[RewardedCooldownUI][BuyBoosterDialog] state changed', {
        cooldown_id: this.rewarded_cooldown_id,
        cooldown_active,
        timer_text,
        button_children: this.reward_btn.getChildren().map( child => child.name() ),
        dialog_children: this.getChildren().map( child => child.name() ),
      } );
    }

    const disabled = cooldown_active || this.reward_inflight;
    const ads_offline = areAdsBlockedOffline();
    this.reward_btn.setClickable( !disabled );
    this.reward_btn.setImgId(
      disabled || ads_offline ? ImgID.UI_NonActiveBtn : ImgID.UI_PurpleFrameBtn,
      disabled || ads_offline ? "non_active_btn" : "frame_btn"
    );
    if ( ads_offline !== this.shown_ads_offline )
    {
      this.shown_ads_offline = ads_offline;
      if ( !ads_offline ) hideOfflineAdTooltip();
      this.invalidateSubtree();
    }
  }

  public override cleanup(): void
  {
    hideOfflineAdTooltip();
    if ( this.reward_btn && this.reward_active_content && this.reward_cooldown_content )
    {
      this.reward_btn.removeChild( this.reward_active_content );
      this.reward_btn.removeChild( this.reward_cooldown_content );
      this.reward_active_content.cleanup();
      this.reward_cooldown_content.cleanup();
    }
    super.cleanup();
  }

  private async watchRewardedAd( btn: UINineSliceElement ): Promise<void>
  {
    if ( this.reward_inflight || isRewardedCooldownActive( this.rewarded_cooldown_id ) )
      return;

    if ( areAdsBlockedOffline() )
    {
      showOfflineAdTooltip( btn );
      return;
    }

    this.reward_inflight = true;
    this.reward_offer_clicked = true;
    btn.setImgId( ImgID.UI_NonActiveBtn, "non_active_btn" );

    void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_OFFER_CLICKED );

    let reward_earned = false;
    try
    {
      const result = await showRewarded( this.rewarded_placement );
      if ( !result.rewarded )
      {
        void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_AD_RESULT, {
          status: result.refused ? "skipped" : "unavailable",
          shown: result.shown,
          rewarded: false,
        } );
        if ( !result.shown ) showAdUnavailableTooltip( btn );
        return;
      }

      void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_AD_RESULT, {
        status: "completed",
        shown: true,
        rewarded: true,
      } );
      reward_earned = true;

      if ( !result.ad_id )
        throw new Error( "Rewarded ad completed without an ad id" );

      const reward = await this.rewardBoosterWithRetry( result.ad_id );
      adoptEconomyFromPlayer( reward.player );
      startRewardedCooldown( this.rewarded_cooldown_id );
      this.on_reward?.( reward.granted.quantity );

      this.reward_granted = true;
      void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_GRANTED, {
        status: "granted",
        shown: true,
        rewarded: true,
        idempotent: reward.idempotent,
      } );

      void logRewardedCompleted( {
        placement: this.rewarded_placement,
        reward_type: this.visual_config.analytics_id,
        reward_value: reward.granted.quantity,
      } );
      DialogManager.closeDialog( "buy_booster_rewarded" );
    }
    catch ( e )
    {
      console.warn( "[BuyBoosterDialog] rewarded booster failed", e );
      void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_AD_RESULT, {
        status: reward_earned ? "grant_error" : "error",
        rewarded: reward_earned,
        ...( reward_earned ? { shown: true } : {} ),
      } );
    }
    finally
    {
      this.reward_inflight = false;
      this.syncRewardButtonState();
    }
  }

  private async rewardBoosterWithRetry( ad_id: string ): Promise<RewardBoosterResponse>
  {
    let last_error: unknown;
    for ( let attempt = 0; attempt < 2; attempt++ )
    {
      try
      {
        return await rewardBooster(
          this.visual_config.analytics_id,
          this.rewarded_placement,
          ad_id
        );
      }
      catch ( error )
      {
        last_error = error;
        if ( attempt === 0 )
          await new Promise( resolve => window.setTimeout( resolve, 300 ) );
      }
    }

    throw last_error;
  }

  private trackRewardedFunnelEvent(
    event_name:
      | AnalyticsEventName.BOOSTER_REWARD_OFFER_SHOWN
      | AnalyticsEventName.BOOSTER_REWARD_OFFER_CLICKED
      | AnalyticsEventName.BOOSTER_REWARD_AD_RESULT
      | AnalyticsEventName.BOOSTER_REWARD_GRANTED
      | AnalyticsEventName.BOOSTER_REWARD_OFFER_CLOSED,
    extra: {
      status?: string;
      close_reason?: string;
      shown?: boolean;
      rewarded?: boolean;
      idempotent?: boolean;
    } = {}
  ): Promise<void>
  {
    return logBoosterRewardedFunnelEvent( event_name, {
      placement: this.rewarded_placement,
      source: this.reward_source,
      booster_type: this.visual_config.analytics_id,
      reward_value: this.reward_quantity,
      ...extra,
    } );
  }

  public override onDialogOpened(): void
  {
    super.onDialogOpened();
    console.log( '[RewardedCooldownUI][BuyBoosterDialog] opened', {
      cooldown_id: this.rewarded_cooldown_id,
      cooldown_active: isRewardedCooldownActive( this.rewarded_cooldown_id ),
      timer_text: this.shown_reward_cooldown_timer,
      dialog_children: this.getChildren().map( child => child.name() ),
    } );
    if ( !this.reward_available || this.reward_offer_shown_tracked )
      return;

    this.reward_offer_shown_tracked = true;
    void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_OFFER_SHOWN );
  }

  public override onDialogCloseRequested( reason: string ): void
  {
    console.log( '[RewardedCooldownUI][BuyBoosterDialog] close requested', {
      cooldown_id: this.rewarded_cooldown_id,
      reason,
      cooldown_active: isRewardedCooldownActive( this.rewarded_cooldown_id ),
      timer_text: this.shown_reward_cooldown_timer,
    } );
    if ( !this.reward_available || this.reward_offer_closed_tracked )
      return;

    this.reward_offer_closed_tracked = true;
    void this.trackRewardedFunnelEvent( AnalyticsEventName.BOOSTER_REWARD_OFFER_CLOSED, {
      status: this.reward_granted
        ? "reward_granted"
        : this.reward_offer_clicked ? "after_click" : "without_click",
      close_reason: reason,
    } );
  }

  private createCloseButton(): void
  {
    const design_size = 138;
    const btn = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 896 ),
      LayoutScaler.yFromDesign( 340 ),
      LayoutScaler.widthFromDesign( design_size ),
      LayoutScaler.heightFromDesign( design_size ),
      ImgID.UI_SettingsDialogCloseBtn,
      undefined,
      true
    );
    btn.setOnClick( () => DialogManager.closeDialog( "close_button" ) );
    this.addChild( btn );
    this.close_btn = btn;
  }

  protected override playAppearAnimation(
    from_scale = 0.7,
    overshoot_scale = 1.1,
    final_scale = 1.0,
    scale_up_duration = 136,
    settle_duration = 96
  ): void
  {
    this.close_btn.setScale( new Vector2( 0, 0 ) );
    super.playAppearAnimation(
      from_scale,
      overshoot_scale,
      final_scale,
      scale_up_duration,
      settle_duration
    );

    this.popCloseButtonAfterAppear( this.close_btn, scale_up_duration + settle_duration );
  }

  public override containsPointGlobal( p: Vector2 ): boolean
  {
    const body_pos = this.pixelPosForRender();
    const body_scale = this.getScaleImportant();
    const body_center_x = body_pos.x + this.rect.getW() / 2;
    const body_center_y = body_pos.y + this.rect.getH() / 2;

    const hit_x = LayoutScaler.xFromDesign( BuyBoosterDialog.HIT_X );
    const hit_y = LayoutScaler.yFromDesign( BuyBoosterDialog.HIT_Y );
    const hit_w = LayoutScaler.widthFromDesign( BuyBoosterDialog.HIT_W );
    const hit_h = LayoutScaler.heightFromDesign( BuyBoosterDialog.HIT_H );

    const left = body_center_x + ( hit_x - this.rect.getW() / 2 ) * body_scale.x;
    const top = body_center_y + ( hit_y - this.rect.getH() / 2 ) * body_scale.y;
    const right = left + hit_w * body_scale.x;
    const bottom = top + hit_h * body_scale.y;

    return p.x >= left && p.x <= right && p.y >= top && p.y <= bottom;
  }

  public name(): string
  {
    return "BuyBoosterDialog";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    if ( this.reward_debug_render_reason )
    {
      console.log( '[RewardedCooldownUI][BuyBoosterDialog] full render', {
        reason: this.reward_debug_render_reason,
        cooldown_id: this.rewarded_cooldown_id,
        timer_text: this.shown_reward_cooldown_timer,
        dialog_children: this.getChildren().map( child => ( {
          name: child.name(),
          should_render: child.shouldBeRendered(),
        } ) ),
      } );
      this.reward_debug_render_reason = null;
    }

    this.startRender();
    this.finishRender();
  }
}
